import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { SingleTicketIcon } from "ui/Icon/DSIcon/SingleTicket"
import { MultiTicketsIcon } from "ui/Icon/DSIcon/MultiTickets"
import { RentalIcon } from "ui/Icon/DSIcon/RentalIcon"
import { AVAILABILITY_STATUSES, STATUS_CHIPS } from "../../constants"
import { getChipReasons, normalizeListingType } from "../../utils"
import ReasonChip from "../ReasonChip"
import styles from "./index.module.css"

/**
 * Listing type -> its icon, the same glyphs the experience listing table uses (Figma 9610:146846).
 *
 * Each is drawn on its own viewBox, none of them square, so a 14×14 box would squash the wider
 * ones. `preserveAspectRatio` handles that: both dimensions are set to the design's 14px box and
 * the glyph scales uniformly inside it. Grey rather than the brand blue the listing table uses —
 * in this pill the icon reads as part of the label.
 */
const TYPE_ICON = {
  private: SingleTicketIcon,
  ticketed: MultiTicketsIcon,
  rental: RentalIcon,
  rentals: RentalIcon
}

/** The pill says "Private", not the API's own `private_trip`. */
const TYPE_LABEL = {
  private: { key: "availability.listing_type_private", fallback: "Private" },
  ticketed: { key: "availability.listing_type_ticketed", fallback: "Ticketed" },
  rental: { key: "availability.listing_type_rental", fallback: "Rental" },
  rentals: { key: "availability.listing_type_rental", fallback: "Rental" }
}

const STATUS_CLASS = {
  [AVAILABILITY_STATUSES.AVAILABLE]: styles.available,
  [AVAILABILITY_STATUSES.LIMITED]: styles.limited,
  [AVAILABILITY_STATUSES.FULLY_BOOKED]: styles.fullyBooked,
  [AVAILABILITY_STATUSES.NOT_AVAILABLE]: styles.notAvailable
}

/**
 * The card at the top of the day panel: which listing, and how the day reads for it.
 *
 * It carries the same background as the day's cell in the grid, so opening a cell lands on a panel
 * that looks like the thing that was clicked. The reason chips repeat the cell's chips for the same
 * reason — the panel then explains each of them in the sections below.
 */
const ListingSummaryCard = ({ listing, day }) => {
  const { t } = useTranslation()

  // Private is the fallback both ways round: it is the type a listing has when nothing says
  // otherwise, and an unknown type reading as "Private" beats an empty pill.
  const listingType = normalizeListingType(listing)
  const TypeIcon = TYPE_ICON[listingType] || SingleTicketIcon
  const typeLabel = TYPE_LABEL[listingType] || TYPE_LABEL.private
  const statusChip = STATUS_CHIPS[day?.status]
  const reasons = getChipReasons(day?.reasons)
  const imageUrl = listing?.image_url || listing?.cover_photo

  return (
    <Flex gap="var(--spacing-xs)" className={`${styles.summaryCard} ${STATUS_CLASS[day?.status] || ""}`}>
      {/* The feed does not send a thumbnail yet; the frame keeps the slot either way. */}
      {imageUrl ? (
        <img src={imageUrl} alt="" className={styles.thumbnail} />
      ) : (
        <span className={`${styles.thumbnail} ${styles.thumbnailEmpty}`}>
          <Icon name="calendar" type="ds" width={24} height={24} color="var(--color-text-tertiary-light)" />
        </span>
      )}

      <Flex vertical gap="var(--spacing-xs)" className={styles.summaryBody}>
        <Flex gap="var(--spacing-xs)" align="flex-start">
          <span className={styles.summaryTitle}>{listing?.title}</span>
          {listing?.listing_type && (
            <span className={styles.typeTag}>
              <TypeIcon width={14} height={14} fill="var(--color-text-secondary-dark)" />
              {t(typeLabel.key, { defaultValue: typeLabel.fallback })}
            </span>
          )}
        </Flex>

        <Flex gap="var(--spacing-xxs)" align="center" wrap className={styles.summaryChips}>
          {statusChip && (
            <span className={`${styles.statusChip} ${day.status === AVAILABILITY_STATUSES.LIMITED ? styles.statusChipMuted : ""}`}>
              {statusChip.icon && (
                <Icon name={statusChip.icon} width={16} height={16} color={statusChip.iconColor || "var(--color-text-secondary-dark)"} />
              )}
              <span style={statusChip.iconColor ? { color: statusChip.iconColor } : undefined}>
                {t(statusChip.labelKey, { defaultValue: statusChip.labelDefault })}
              </span>
            </span>
          )}
          {reasons.map((reason, index) => (
            <ReasonChip key={`${reason.type}-${reason.blackout_id || index}`} reason={reason} />
          ))}
        </Flex>
      </Flex>
    </Flex>
  )
}

ListingSummaryCard.propTypes = {
  /** The listing as the availability feed describes it. */
  listing: PropTypes.shape({
    title: PropTypes.string,
    listing_type: PropTypes.string,
    image_url: PropTypes.string,
    cover_photo: PropTypes.string
  }),
  day: PropTypes.shape({ status: PropTypes.string, reasons: PropTypes.array })
}

export default ListingSummaryCard
